import format from 'date-fns/format';
import { isEmpty, includes, get, identity } from 'lodash';
// import { addBreadcrumb } from '../tracker/raven';

type LoggerLevel = 0 | 1 | 2 | 3 | 4;
type LoggerMethodName = 'debug' | 'log' | 'warn' | 'error';
interface LoggerPlugin {
    factory?: (logger: Logger) => void;
}
type LoggerContext = Record<string, unknown>;
interface LoggerStyles {
    [key: string]: unknown;
    level?: Record<number, string>;
}
interface LoggerParams {
    title: string;
    atLevel?: LoggerLevel;
    parent?: Logger | null;
    styles?: LoggerStyles;
    blackList?: string[];
    context?: LoggerContext;
}

interface FactoryPayload {
    timestamp: Date;
    level: LoggerLevel;
    title: string;
    context: LoggerContext;
    message: string;
    extra?: unknown;
}

export default class Logger {
    debug!: (...args: unknown[]) => void;
    log!: (...args: unknown[]) => void;
    warn!: (...args: unknown[]) => void;
    error!: (...args: unknown[]) => void;

    private _title: string;
    private _level: LoggerLevel;
    private _parent: Logger | null;
    private _styles: LoggerStyles;
    private _children: Map<string, Logger>;
    private _archive: unknown[];
    private _context: LoggerContext;
    private _blackList: string[];
    private _plugins: LoggerPlugin[];

    constructor({
        title,
        atLevel = Logger.levels.LOG as LoggerLevel,
        parent = null,
        styles = {},
        blackList = [],
        context = {}
    }: LoggerParams) {
        this._title = title;
        this._level = atLevel;
        this._parent = parent;
        const parentStyles = parent ? parent._styles : {};
        this._styles = { ...parentStyles, ...styles };
        this._children = new Map();
        this._archive = [];

        const parentContext = parent ? parent._context : {};
        this._context = { ...parentContext, ...context };
        this._blackList = ['context'].concat(blackList);
        this._assignMethods();
        this._plugins = [];
    }

    static levelNames = {
        4: 'DEBUG',
        3: 'LOG',
        2: 'WARN',
        1: 'ERROR',
        0: 'SILENT'
    };

    static levels = {
        DEBUG: 4,
        LOG: 3,
        WARN: 2,
        ERROR: 1,
        SILENT: 0
    };

    methodsMap: Record<LoggerMethodName, LoggerLevel> = {
        debug: Logger.levels.DEBUG as LoggerLevel,
        log: Logger.levels.LOG as LoggerLevel,
        warn: Logger.levels.WARN as LoggerLevel,
        error: Logger.levels.ERROR as LoggerLevel
    };

    /**
     * Assigns logger methods to class instance
     */
    _assignMethods() {
        (Object.keys(this.methodsMap) as LoggerMethodName[]).forEach(methodName => {
            Object.assign(this, {
                [methodName]: (...args: unknown[]) => {
                    const level = this.methodsMap[methodName];
                    this._print(methodName, level, ...args);
                }
            });
        });
    }

    /**
     * @param {Number} level
     * @param  {...unknown} message - Extra arguments
     */
    _factory(level: LoggerLevel, ...message: unknown[]): FactoryPayload {
        let extra: unknown;

        if (message.length > 1) {
            const last = message[message.length - 1];

            if (typeof last === 'object' && last !== null && !Array.isArray(last)) {
                extra = message.pop();
            }
        }

        return {
            timestamp: new Date(),
            level,
            title: this._title,
            context: this._context,
            message: message.join(' '),
            extra
        };
    }

    _formatters = {
        timestamp: (value: Date) => `[${format(value, 'HH:mm:ss')}]`,
        level: (value: LoggerLevel) => Logger.levelNames[value]
    };

    /**
     * @param {Object} params
     * @returns {Array} - Compiled log message
     */
    _formatMessage(params: Record<string, unknown>) {
        const result: string[] = [];
        const styles: string[] = [];
        Object.keys(params).forEach(paramName => {
            if (includes(this._blackList, paramName)) {
                return;
            }
            const formatter = get(this._formatters, paramName, identity) as (v: unknown) => unknown;
            const val = formatter(params[paramName]);

            if (this._styles[paramName]) {
                result.push(`%c${val}`);
                const levelValue = Number(params.level);
                const levelStyles = this._styles.level || {};
                if (paramName === 'level') {
                    styles.push(levelStyles[levelValue] || '');
                } else {
                    styles.push(String(this._styles[paramName]));
                }
            } else {
                result.push(`${val} `);
            }
        });

        return [result.join('').trim(), ...styles];
    }

    /**
     * @param {String} methodName - Log method
     * @param {Number} methodLevel - Level
     * @param  {*} args - Extra arguments
     */
    _print(methodName: LoggerMethodName, methodLevel: LoggerLevel, ...args: unknown[]) {
        if (methodLevel > this.level) {
            return;
        }

        const data = this._factory(methodLevel, ...args);
        const { extra, ...messageFields } = data;
        const formattedMessage = this._formatMessage(messageFields);

        console[methodName](...formattedMessage);
        if (extra) {
            console[methodName](extra);
        }
    }

    use(plugin: LoggerPlugin) {
        this._plugins.push(plugin);
        if (plugin.factory) {
            plugin.factory(this);
        }
    }

    /**
     * Creates a new children logger
     * @param {*} params - Logger initial parameters
     * @returns {Class} - Logger
     */
    createLogger(params: Omit<LoggerParams, 'parent'>) {
        const logger = new Logger({ ...params, parent: this });
        this._plugins.forEach(plugin => logger.use(plugin));
        this._children.set(logger.title, logger);

        return logger;
    }

    /**
     * @returns {String} - Logger title
     */
    getTitle() {
        return this._title;
    }

    /**
     * @returns {Number} - Level
     */
    getLevel() {
        return this._level;
    }

    /**
     * Set logger's level
     * @param {Number} newLevel - Log level
     */
    setLevel(newLevel: LoggerLevel) {
        this._level = newLevel;
    }

    /**
     * Set logger's level including children loggers
     * @param {Number} newLevel - Log level
     */
    setLevelAll(newLevel: LoggerLevel) {
        this.setLevel(newLevel);

        if (!this._children.size) return;

        this._children.forEach(logger => logger.setLevelAll(newLevel));
    }

    /**
     * Put logger to silent
     */
    off() {
        this._level = Logger.levels.SILENT as LoggerLevel;
    }

    /**
     * Put logger to silent including children loggers
     */
    offAll() {
        this._level = Logger.levels.SILENT as LoggerLevel;

        if (!this._children.size) return;

        this._children.forEach(logger => logger.offAll());
    }

    /**
     * Enables highest level of logging
     */
    on() {
        this._level = Logger.levels.DEBUG as LoggerLevel;
    }

    /**
     * Enables highest level of logging including children loggers
     */
    onAll() {
        this._level = Logger.levels.LOG as LoggerLevel;

        if (!this._children.size) return;

        this._children.forEach(logger => logger.onAll());
    }

    setContext(context: LoggerContext) {
        if (isEmpty(context)) {
            this._context = {};
        } else {
            Object.assign(this._context, context);
        }
    }

    getContext() {
        return this._context;
    }

    get level() {
        return this._level;
    }

    get title() {
        return this._title;
    }
}
